import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
}

export const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = "Select option...",
      searchPlaceholder = "Search...",
      emptyText = "No options found.",
      disabled = false,
      className,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const commandListRef = React.useRef<HTMLDivElement>(null);
    const selectedOption = options.find((option) => option.value === value);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label={placeholder}
            className={cn("w-full justify-between", className)}
            disabled={disabled}
          >
            {selectedOption ? selectedOption.label : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <Command
            filter={(value, search, keywords) => {
              // check if value or keywords include search
              if (
                value.toLowerCase().includes(search.toLowerCase()) ||
                keywords?.some((keyword) =>
                  keyword.toLowerCase().includes(search.toLowerCase())
                )
              ) {
                return 1;
              }
              return 0;
            }}
          >
            <CommandInput
              onKeyDown={(e) => {
                // scroll to top when typing or clearing search input
                if (
                  /^[\w\d\s\p{P}\p{S}]$/u.test(e.key) || // only do for alphanumeric characters
                  /[0-9]/.test(e.key) || // and numbers
                  e.key === "Backspace" ||
                  e.key === "Delete"
                ) {
                  const list = document.querySelector(
                    "[data-slot='command-list']"
                  ) as HTMLDivElement;

                  if (list) {
                    list.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }
                }
              }}
              placeholder={searchPlaceholder}
            />

            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandList ref={commandListRef}>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={(currentValue) => {
                      onValueChange(currentValue === value ? "" : currentValue);
                      setOpen(false);
                    }}
                    keywords={
                      [option.label, option.description].filter(
                        Boolean
                      ) as string[]
                    }
                    disabled={option.disabled}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      {option.description && (
                        <span className="text-xs text-muted-foreground">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

Combobox.displayName = "Combobox";
